import React from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import AppHeader from '../../components/ui/AppHeader';
import ArchitectHeader from '../../components/pm/ArchitectHeader';
import EngineerHeader from '../../components/pm/EngineerHeader';
import CpaHeader from '../../components/finances/CpaHeader';
import {
  canAccessProposalRatesSettings,
  isDesignerRole,
  isFullCrmEmployeeAccess,
  isInternal1099ContractorUser,
  isScopedInternalEmployeeRole,
} from '../../utils/userRoles';
import Contractor1099Header from '../../components/pm/Contractor1099Header';
import SettingsSidebar from '../../components/settings/SettingsSidebar';
import { useAuth } from '../../utils/AuthContext';
import { useCpaPortalPreview } from '../../hooks/useCpaPortalPreview';
import { getSettingsNavItem, SETTINGS_NAV_ITEMS } from '../../components/settings/settingsNav';
import '../../components/pm/pm-shared.css';
import '../OperationsHub.css';
import '../../components/ops/OpsSidebar.css';
import './SettingsPage.css';

function getSettingsSectionFromPath(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] === 'settings' && parts[1]) return parts[1];
  return 'account';
}

const SettingsMobileNav: React.FC<{
  isAdmin: boolean;
  accountOnly?: boolean;
  allowedSectionIds?: string[] | null;
}> = ({ isAdmin, accountOnly, allowedSectionIds }) => {
  const location = useLocation();
  const section = getSettingsSectionFromPath(location.pathname);
  const navigate = useNavigate();
  const items = SETTINGS_NAV_ITEMS.filter(i => {
    if (allowedSectionIds) return allowedSectionIds.includes(i.id);
    if (accountOnly) return i.id === 'account';
    return !i.adminOnly || isAdmin;
  });

  return (
    <div className="settings-mobile-nav">
      <label className="sr-only" htmlFor="settings-mobile-section">
        Settings section
      </label>
      <select
        id="settings-mobile-section"
        value={section || 'account'}
        onChange={e => navigate(`/settings/${e.target.value}`)}
      >
        {items.map(item => (
          <option key={item.id} value={item.id}>
            {item.label}
          </option>
        ))}
      </select>
    </div>
  );
};

const SettingsLayout: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { isCpaShell } = useCpaPortalPreview();
  const section = getSettingsSectionFromPath(location.pathname);
  const isAdmin = user?.role === 'admin';
  const hasFullEmployeeAccess = isFullCrmEmployeeAccess({
    authRole: user?.role,
    designations: user?.designations,
  });
  const isInternal1099 = isInternal1099ContractorUser({
    role: user?.role,
    is1099Contractor: user?.is1099Contractor,
  });
  const salesProposalSettings =
    isScopedInternalEmployeeRole(user?.role)
    && !hasFullEmployeeAccess
    && canAccessProposalRatesSettings(user?.role, user?.designations);
  const accountOnly =
    user?.role === 'cpa'
    || user?.role === 'designer'
    || user?.role === 'engineer'
    || isInternal1099
    || (isScopedInternalEmployeeRole(user?.role) && !hasFullEmployeeAccess && !salesProposalSettings);
  const allowedSectionIds = accountOnly
    ? ['account']
    : salesProposalSettings
      ? ['account', 'proposals']
      : null;

  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'supplier') return <Navigate to="/" replace />;

  if (section && !getSettingsNavItem(section)) {
    return <Navigate to="/settings/account" replace />;
  }

  if (allowedSectionIds && !allowedSectionIds.includes(section || 'account')) {
    return <Navigate to="/settings/account" replace />;
  }

  if (section === 'admin' && !isAdmin) {
    return <Navigate to="/settings/account" replace />;
  }

  const navItem = getSettingsNavItem(section);
  if (navItem?.adminOnly && !isAdmin) {
    return <Navigate to="/settings/account" replace />;
  }

  return (
    <div className="ops-hub settings-hub">
      {isCpaShell ? (
        <CpaHeader />
      ) : isInternal1099 ? (
        <Contractor1099Header />
      ) : isDesignerRole(user.role) ? (
        <ArchitectHeader />
      ) : user.role === 'engineer' ? (
        <EngineerHeader />
      ) : (
        <AppHeader />
      )}
      <div className="ops-hub-layout settings-hub-layout">
        <SettingsMobileNav isAdmin={isAdmin} accountOnly={accountOnly} allowedSectionIds={allowedSectionIds} />
        <div className="settings-hub-body">
          <SettingsSidebar isAdmin={isAdmin} accountOnly={accountOnly} allowedSectionIds={allowedSectionIds} />
          <main className="settings-main pm-scroll" id="settings-main-content">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};

export default SettingsLayout;
